import React from 'react';
import PropTypes from 'prop-types';
import genericHelpers from '../../../_utils/generic-helpers';
import SliderScale, {
  TICK_INCREMENT_PC,
} from '../../common/advanced-editor/slider-scale';
import {
  percentageAsValue,
  valueAsPercentage,
} from '../../../_utils/tools/slider-control-conversions';
import DisplayEditValue from './display-edit-value';
import { EDIT_HEADER_TITLES } from '../../common/advanced-editor/constants';

class SliderControl extends React.PureComponent {
  constructor(props) {
    super(props);

    this.startPos = null;
    this.previousPos = null;

    this.state = {
      sliderActive: false,
      sliderValue: this.props.value,
    };

    this.setScaleWrapper = element => {
      this.scaleWrapper = element;
    };
    this.setSliderControlContainer = element => {
      this.sliderControl = element;
    };
    this.setSliderFocusPoint = element => {
      this.sliderFocusPoint = element;
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyPress);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress);
  }

  getSliderStartPos = () => {
    const { left: sliderOffset } = this.sliderControl.getBoundingClientRect();
    const { right, width } = this.scaleWrapper.getBoundingClientRect();
    const startOffset = right - sliderOffset;

    return Math.abs(startOffset - width * 1.5);
  };

  getSliderPercentage = () => {
    const { width } = this.scaleWrapper.getBoundingClientRect();
    return (this.getSliderStartPos() / width) * 100;
  };

  handleKeyPress = e => {
    if (![37, 39].includes(e.keyCode)) {
      return e;
    }

    const startPos = this.getSliderStartPos();

    if (e.keyCode === 37) {
      // Arrow Left
      this.sliderControl.scrollLeft = startPos - TICK_INCREMENT_PC;
    }

    if (e.keyCode === 39) {
      // Arrow Right
      this.sliderControl.scrollLeft = startPos + TICK_INCREMENT_PC;
    }

    this.handleChange();
  };

  handleScroll = e => {
    e.preventDefault();
    const { offsetLeft } = this.sliderControl;
    const mouseMovement = e.pageX - offsetLeft;
    const distance = mouseMovement - this.startPos;
    this.sliderControl.scrollLeft = this.previousPos - distance;
  };

  handleChange = () => {
    const { onChange, threshold, min, max, disabled } = this.props;

    if (disabled) {
      return;
    }

    const percentage = this.getSliderPercentage();
    const value = percentageAsValue({ percentage, min, max });

    genericHelpers.debounce(onChange(value), threshold);
    this.setState({ sliderValue: value });
  };

  handleMouseDown = e => {
    const { offsetLeft, scrollLeft } = this.sliderControl;
    this.setState({ sliderActive: true });
    this.startPos = e.pageX - offsetLeft;
    this.previousPos = scrollLeft;
  };

  handleLeave = () => {
    this.setState({ sliderActive: false });
  };

  handleMouseMove = e => {
    const { disabled } = this.props;
    const { sliderActive } = this.state;

    if (!sliderActive || disabled) {
      return;
    }

    this.handleScroll(e);
    this.handleChange();
  };

  set = value => {
    const { min, max } = this.props;
    const { width } = this.sliderControl.getBoundingClientRect();
    const percentage = valueAsPercentage({ value, min, max });

    this.sliderControl.scrollLeft = (width / 100) * percentage;
    this.setState({ sliderValue: value });

    // Set timeout for Mobile device focus
    setTimeout(() => {
      this.sliderFocusPoint.focus();
    }, 1);
  };

  render() {
    const { onChange, min, max, editMode } = this.props;

    if (!onChange) {
      return null;
    }

    const { sliderValue } = this.state;
    const sliderDisplayValue = DisplayEditValue.toFixedString(sliderValue);

    return (
      <div className="slider-control__wrapper">
        <span className="slider-control__line badge badge-pill badge-primary"></span>
        <div
          className="slider-control position-relative"
          ref={this.setSliderControlContainer}
          tabIndex="0">
          <div
            ref={this.setSliderFocusPoint}
            role="slider"
            tabIndex="0"
            className="slider-control__scale-wrapper"
            aria-valuetext={`Current value for ${EDIT_HEADER_TITLES[editMode]} is ${sliderDisplayValue}`}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={Number(sliderDisplayValue)}
            aria-label="Swipe left or right with two fingers to adjust"
            onMouseDown={this.handleMouseDown}
            onMouseMove={this.handleMouseMove}
            onMouseUp={this.handleLeave}
            onMouseLeave={this.handleLeave}
            onTouchMove={this.handleChange}>
            <SliderScale
              setRef={this.setScaleWrapper}
              className="slider-control__scale"
            />
          </div>
        </div>
      </div>
    );
  }
}

export default SliderControl;

SliderControl.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  threshold: PropTypes.number,
  onChange: PropTypes.func,
};

SliderControl.defaultProps = {
  min: 0,
  max: 0,
  threshold: 0,
  onChange: null,
};
